import React from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import { Formik, Form } from 'formik';

import { FormField, FormSubmit } from 'core/components/form';
import { Label } from 'core/components/label';
import withDatePicker from 'core/components/with-date-picker';
import useStyles from '../../people/add-form.styles';
import { addLossHistoryValidationSchema } from './loss-history.validation';
import { buildManualEntry } from './helpers';

// Prefix the DOM ids so this form's inputs never collide with other add-forms on the same page
// (see the same guard in add-farm-interested-party-form.jsx / #32617).
const ID_PREFIX = 'loss-history-add-';

const initialValues = {
  causeOfLoss: '',
  lossDate: '',
  carrierName: '',
  claimNumber: '',
  policyNumber: '',
  policyType: '',
  claimStatus: '',
  catastropheNumber: '',
  paidAmount: '',
  lossAmount: '',
  property: '',
  comments: '',
  lossDescription: ''
};

// #33939 — Manually add a loss entry when no report data exists. The entry is stamped
// source = "Manual Entry"; `addedBy` is set SERVER-SIDE on save from the acting user's identity
// (not trusted from the client). Who may reach this form is gated by the section (UW on policies).
const AddLossHistoryForm = ({ onAdd, disabled }) => {
  const classes = useStyles();

  const handleSubmit = (values, { resetForm }) => {
    onAdd(buildManualEntry(values));
    resetForm();
  };

  return (
    <Formik initialValues={initialValues} validationSchema={addLossHistoryValidationSchema} onSubmit={handleSubmit}>
      <Form disabled={disabled} className={classes.container} id="addLossHistory">
        <Label type="formTitle">Add Loss History Entry</Label>
        <Grid container spacing={4}>
          <FormField
            type="string"
            id={`${ID_PREFIX}causeOfLoss`}
            name="causeOfLoss"
            label="Cause of Loss"
            mode="light"
            xs={6}
          />
          <FormField
            type="date"
            id={`${ID_PREFIX}lossDate`}
            name="lossDate"
            label="Loss Date"
            defaultToEmpty
            mode="light"
            xs={6}
          />
          <FormField
            type="string"
            id={`${ID_PREFIX}carrierName`}
            name="carrierName"
            label="Carrier Name"
            mode="light"
            xs={6}
          />
          <FormField
            type="string"
            id={`${ID_PREFIX}claimNumber`}
            name="claimNumber"
            label="Claim Number"
            mode="light"
            xs={6}
          />
          <FormField
            type="string"
            id={`${ID_PREFIX}policyNumber`}
            name="policyNumber"
            label="Policy Number"
            mode="light"
            xs={6}
          />
          <FormField
            type="string"
            id={`${ID_PREFIX}policyType`}
            name="policyType"
            label="Policy Type"
            mode="light"
            xs={6}
          />
          <FormField
            type="string"
            id={`${ID_PREFIX}claimStatus`}
            name="claimStatus"
            label="Claim Status"
            mode="light"
            xs={6}
          />
          <FormField
            type="string"
            id={`${ID_PREFIX}catastropheNumber`}
            name="catastropheNumber"
            label="Catastrophe Number"
            mode="light"
            xs={6}
          />
          <FormField
            type="numeric"
            id={`${ID_PREFIX}paidAmount`}
            name="paidAmount"
            label="Paid Amount"
            format={{ thousandSeparator: true, prefix: '$', decimalScale: 2 }}
            mode="light"
            xs={6}
          />
          <FormField
            type="numeric"
            id={`${ID_PREFIX}lossAmount`}
            name="lossAmount"
            label="Loss Amount"
            format={{ thousandSeparator: true, prefix: '$', decimalScale: 2 }}
            mode="light"
            xs={6}
          />
          <FormField type="string" id={`${ID_PREFIX}property`} name="property" label="Property" mode="light" xs={6} />
          <FormField
            type="textarea"
            id={`${ID_PREFIX}comments`}
            name="comments"
            label="Comments"
            mode="light"
            xs={12}
          />
          <FormField
            type="textarea"
            id={`${ID_PREFIX}lossDescription`}
            name="lossDescription"
            label="Loss Description"
            mode="light"
            xs={12}
          />
        </Grid>
        <Grid container justifyContent="flex-end" alignItems="center" spacing={4}>
          <FormSubmit
            mode="big"
            disabled={disabled}
            className={classes.submitButton}
            variant="contained"
            color="secondary"
            xs={4}
          >
            Add Entry
          </FormSubmit>
        </Grid>
      </Form>
    </Formik>
  );
};

AddLossHistoryForm.propTypes = {
  onAdd: PropTypes.func.isRequired,
  disabled: PropTypes.bool
};

AddLossHistoryForm.defaultProps = {
  disabled: false
};

// FormField type="date" renders @material-ui/pickers, which requires MuiPickersUtilsProvider.
export default withDatePicker(AddLossHistoryForm);
